import { useState } from "react";
import { UNSCORED_TYPES } from "../../domain/evaluate";
import { toggleSet } from "../../domain/mechanics";
import { speakEnglish } from "./audio";
import { SceneCard } from "./SceneCard";
import { POSITION_LABEL, type PlayItem } from "./types";
import { VoiceCompare } from "./VoiceCompare";

const MULTI_TYPES = new Set(["costumes", "position", "graphemes"]);
const PICTURE_TYPES = new Set(["word", "picture"]);
const SOUND_LABEL_TYPES = new Set(["listen", "contrast", "motor", "first_sound"]);

function optionLabel(type: string, opt: string): string {
  if (opt === "on") return "Да, голос включён";
  if (opt === "off") return "Нет, голос выключен";
  if (SOUND_LABEL_TYPES.has(type) && !opt.includes(" ") && !opt.includes("→") && !opt.includes("—")) {
    return `/${opt}/`;
  }
  return opt;
}

export function SoundsPlayer({
  item,
  onSubmit,
}: {
  item: PlayItem;
  onSubmit: (selected: unknown) => void;
}) {
  const [picked, setPicked] = useState<string[]>([]);
  const options = item.options ?? [];
  const gallery = item.words ?? item.pictures;
  const multi = Boolean(item.multiSelect) || MULTI_TYPES.has(item.type);

  if (item.type === "record") {
    return (
      <div>
        <VoiceCompare word={item.word || item.audio || "fox"} ipa={item.ipa} />
        <button type="button" className="btn" onClick={() => onSubmit("self_check")}>Готово</button>
      </div>
    );
  }

  if (UNSCORED_TYPES.has(item.type) || (item.unscored && options.length === 0)) {
    return (
      <div>
        <div className="tools">
          <button type="button" className="btn ghost" onClick={() => speakEnglish(item.word || item.audio || item.ipa || "")}>Послушать эталон</button>
          <button type="button" className="btn ghost" onClick={() => speakEnglish(item.word || item.audio || "", 0.55)}>Послушать медленнее</button>
        </div>
        {item.ipa ? <p className="ipa-lg">{item.ipa}</p> : null}
        {item.hint ? <p className="muted">{item.hint}</p> : null}
        {gallery?.length ? (
          <div className="pic-grid">
            {gallery.map((w) => <SceneCard key={w} word={w} caption={w} />)}
          </div>
        ) : item.word ? (
          <SceneCard word={item.word} caption={item.word} />
        ) : null}
        <button type="button" className="btn" onClick={() => onSubmit("self_check")}>Готово</button>
      </div>
    );
  }

  if (multi) {
    const labels = item.type === "position" ? POSITION_LABEL : undefined;
    return (
      <div>
        <div className="tools">
          <button type="button" className="btn ghost" onClick={() => speakEnglish(item.word || item.audio || item.ipa || "")}>Послушать эталон</button>
          <button type="button" className="btn ghost" onClick={() => speakEnglish(item.word || item.audio || "", 0.55)}>Послушать медленнее</button>
        </div>
        {gallery?.length ? (
          <div className="pic-grid">
            {gallery.map((w) => <SceneCard key={w} word={w} caption={w} />)}
          </div>
        ) : item.word ? (
          <SceneCard word={item.word} caption={item.word} />
        ) : null}
        <p className="muted">{item.hint}</p>
        <div className="tools">
          {options.map((opt) => (
            <button
              key={opt}
              type="button"
              className={`btn ghost${picked.includes(opt) ? " on" : ""}`}
              aria-pressed={picked.includes(opt)}
              onClick={() => setPicked(toggleSet(picked, opt))}
            >
              {labels?.[opt] ?? opt}
            </button>
          ))}
        </div>
        <button type="button" className="btn" disabled={picked.length === 0} onClick={() => onSubmit(picked)}>Готово</button>
      </div>
    );
  }

  if (PICTURE_TYPES.has(item.type)) {
    return (
      <div>
        <div className="tools">
          <button type="button" className="btn ghost" onClick={() => speakEnglish(item.ipa || item.audio || "")}>Послушать эталон</button>
        </div>
        <div className="pic-grid">
          {options.map((opt) => (
            <SceneCard key={opt} word={opt} caption={opt} selected={picked[0] === opt} onClick={() => setPicked([opt])} />
          ))}
        </div>
        <button type="button" className="btn" disabled={!picked[0]} onClick={() => onSubmit(picked[0])}>Готово</button>
      </div>
    );
  }

  return (
    <div>
      <div className="tools">
        <button type="button" className="btn ghost" onClick={() => speakEnglish(item.word || item.audio || item.ipa || "")}>Послушать эталон</button>
        <button type="button" className="btn ghost" onClick={() => speakEnglish(item.word || item.audio || "", 0.55)}>Послушать медленнее</button>
      </div>
      {item.ipa ? <p className="ipa-lg">{item.ipa}</p> : null}
      {item.hint ? <p className="muted">{item.hint}</p> : null}
      {item.word && item.type === "sort" ? <SceneCard word={item.word} caption={item.word} /> : null}
      <div className="tools">
        {options.map((opt) => (
          <button
            key={opt}
            type="button"
            className={`btn${picked[0] === opt ? " steel" : ""}`}
            onClick={() => setPicked([opt])}
          >
            {optionLabel(item.type, opt)}
          </button>
        ))}
      </div>
      <button type="button" className="btn" disabled={!picked[0]} onClick={() => onSubmit(picked[0])}>Готово</button>
    </div>
  );
}
